import { CRMChannel, ClientContact } from "@jugl-web/rest-api";
import { EmailConversationModel } from "@jugl-web/rest-api/integrations/models/EmailConversation";
import { InstagramConversationModel } from "@jugl-web/rest-api/integrations/models/InstagramConversation";
import { TwilioConversationModel } from "@jugl-web/rest-api/integrations/models/TwilioConversation";
import { WebChatBotConversationModel } from "@jugl-web/rest-api/integrations/models/WebChatBotConversation";
import { WhatsappConversationModel } from "@jugl-web/rest-api/integrations/models/WhatsappConversation";
import { emailConversations$ } from "@jugl-web/rest-api/integrations/hooks/useEmailConversations";
import { instagramConversations$ } from "@jugl-web/rest-api/integrations/hooks/useInstagramConversations";
import { twilioConversations$ } from "@jugl-web/rest-api/integrations/hooks/useTwilioConversations";
import { webChatBotConversations$ } from "@jugl-web/rest-api/integrations/hooks/useWebChatBotConversations";
import { whatsappConversations$ } from "@jugl-web/rest-api/integrations/hooks/useWhatsappConversations";
import { useCallback } from "react";
import { useClientSupportPageContext } from "../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { ConversationModel } from "../types";

export const useApplyIdentifiedContactToConversation = () => {
  const { selectedConversation, selectedChannel, selectConversation } =
    useClientSupportPageContext();

  return useCallback(
    (contact: ClientContact) => {
      const channelConversations =
        contact.conversations?.filter(
          (conversation) => conversation.src === selectedChannel
        ) ?? [];

      if (channelConversations.length === 0) {
        return;
      }

      const emitUpdate = (conversation: ConversationModel) => {
        switch (selectedChannel) {
          case CRMChannel.whatsapp:
            whatsappConversations$.next({
              type: "update",
              conversation: conversation as WhatsappConversationModel,
            });
            break;
          case CRMChannel.instagram:
            instagramConversations$.next({
              type: "update",
              conversation: conversation as InstagramConversationModel,
            });
            break;
          case CRMChannel.chatbot:
            webChatBotConversations$.next({
              type: "update",
              conversation: conversation as WebChatBotConversationModel,
            });
            break;
          case CRMChannel.twilio:
            twilioConversations$.next({
              type: "update",
              conversation: conversation as TwilioConversationModel,
            });
            break;
          case CRMChannel.email:
            emailConversations$.next({
              type: "update",
              conversation: conversation as EmailConversationModel,
            });
            break;
          default:
            break;
        }
      };

      channelConversations.forEach((linkedConversation) => {
        if (selectedConversation?.conv_id === linkedConversation.conv_id) {
          const updatedConversation: ConversationModel = {
            ...selectedConversation,
            contact_info: contact,
          };
          emitUpdate(updatedConversation);
          selectConversation(updatedConversation, true);
        } else {
          emitUpdate({
            conv_id: linkedConversation.conv_id,
            contact_info: contact,
          } as unknown as ConversationModel);
        }
      });
    },
    [selectConversation, selectedChannel, selectedConversation]
  );
};
